import React, { useEffect, useState, useRef } from "react";

let ACCESS_TOKEN = null;

// Utilities to store/retrieve from sessionStorage safely
function getStoredAccessToken() {
  try {
    return sessionStorage.getItem("AccessToken") || "";
  } catch (error) {
    return "";
  }
}

// Store or remove an Access Token from sessionStorage
function setStoredAccessToken(token) {
  try {
    if (token) {
      sessionStorage.setItem("AccessToken", token);
    } else {
      sessionStorage.removeItem("AccessToken");
    }
  } catch (error) {
    console.error("Failed to access sessionStorage:", error);
  }
}

// Notify other parts of the application that the ACCESS_TOKEN has been updated
function notifyAccessTokenUpdated() {
  try {
    const ev = new CustomEvent("accessToken:updated", {
      detail: { accessToken: ACCESS_TOKEN },
    });
    window.dispatchEvent(ev);
  } catch (error) {
    console.error("Failed to dispatch 'accessToken:updated' event:", error);
  }
}

// Returns the current API key from memory or sessionStorage
export function getAccessToken() {
  return ACCESS_TOKEN || getStoredAccessToken() || "";
}

// Sets the API key (after validation) and notifies listeners
export function setAccessTokenFromUser(token) {
  const t = String(token || "").trim();
  ACCESS_TOKEN = t;
  setStoredAccessToken(t);
  notifyAccessTokenUpdated();
}

// Clears the API key (logout-like behavior)
export function clearAccessToken() {
  ACCESS_TOKEN = "";
  setStoredAccessToken("");
  notifyAccessTokenUpdated();
}

// Validate API key with backend by calling /validate/token
// Returns true for HTTP 200/OK, false otherwise.
async function validateTokenOnServer(token, { signal } = {}) {
  const headers = new Headers();

  let auth = String(token || "").trim();
  // If it doesn't already start with "Bearer " (case-insensitive), add it
  if (!/^Bearer\s+/i.test(auth) && auth !== "") {
    auth = `Bearer ${auth}`;
  }

  headers.set("Authorization", auth);

  try {
    const res = await fetch("/validate/token", {
      method: "GET",
      headers,
      signal,
    });
    return res.ok;
  } catch {
    return false;
  }
}

// A thin wrapper around window.
// fetch that auto-attaches Authorization header if available.
export async function apiFetch(input, init = {}) {
  const headers = new Headers(init.headers || {});
  const token = getAccessToken(); // short-lived access token

  if (token) {
    let auth = String(token).trim();
    // Only prepend "Bearer " if it doesn't already start with it (case-insensitive)
    if (!/^Bearer\s+/i.test(auth)) {
      auth = `Bearer ${auth}`;
    }
    headers.set("Authorization", auth);
  }

  const res = await fetch(input, { ...init, headers });

  // Handle token expiration
  if (res && res.status === 401) {
    try {
      clearAccessToken();
      const ev = new CustomEvent("token:expired");
      window.dispatchEvent(ev);
    } catch (error) {
      console.error("Failed to dispatch 'token:expired' event:", error);
    }
  }

  return res;
}

// - Installs postMessage receiver to accept API key from parent
// - Validates API key with backend (/validate/token) before rendering children
// - Shows a form for direct input and an error message if invalid
export default function ApiKeyGate({ children }) {
  const [accessToken, setAccessToken] = useState("");
  const [inputKey, setInputKey] = useState("");
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  //  FIX 1: ใช้ useRef เพื่อเช็คว่า component ยัง mount อยู่หรือไม่
  const isMountedRef = useRef(true);

  //  FIX 2: ใช้ useRef เพื่อเก็บ AbortController
  const abortControllerRef = useRef(null);

  // Validate and set key helper
  const validateAndSet = async (candidate, checkToken = true) => {
    const input_key = String(candidate || "").trim();

    // ป้องกัน setState หลัง unmount
    if (!isMountedRef.current) return;

    if (!input_key) {
      if (isMountedRef.current) {
        setStatus("error");
        setErrorMsg("กรุณากรอก Secret Key");
      }
      return;
    }

    if (isMountedRef.current) {
      setStatus("validating");
      setErrorMsg("");
    }

    // ยกเลิก request เดิมถ้ามี
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // สร้าง AbortController ใหม่
    const ac = new AbortController();
    abortControllerRef.current = ac;

    let token = null;

    if (!checkToken) {
      try {
        const res = await fetch("/auth/token", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "SECRET-KEY": input_key,
          },
          body: JSON.stringify({}),
          signal: ac.signal,
        });

        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          token = data && data.access_token ? String(data.access_token) : null;
        }
      } catch (err) {
        // ถ้าถูก abort ไม่ต้องทำอะไร
        if (err.name === "AbortError") {
          console.log("Fetch aborted");
          return;
        }
        token = null;
      }
    } else {
      token = input_key;
    }

    // เช็คว่า component ยัง mount อยู่ก่อน setState
    if (!isMountedRef.current) return;

    if (token) {
      try {
        const token_validated = await validateTokenOnServer(token, {
          signal: ac.signal,
        });

        // เช็คอีกครั้งหลัง await
        if (!isMountedRef.current) return;

        if (token_validated) {
          setAccessTokenFromUser(token);
          setAccessToken(token);
          setStatus("idle");
          setErrorMsg("");
        } else {
          clearAccessToken();
          setAccessToken("");
          setStatus("error");
          setErrorMsg("Token ไม่ถูกต้อง กรุณาลองใหม่");
        }
      } catch (err) {
        if (err.name === "AbortError") {
          console.log("Validation aborted");
          return;
        }

        if (isMountedRef.current) {
          clearAccessToken();
          setAccessToken("");
          setStatus("error");
          setErrorMsg("เกิดข้อผิดพลาดในการตรวจสอบ Token");
        }
      }
    } else {
      if (isMountedRef.current) {
        clearAccessToken();
        setAccessToken("");
        setStatus("error");
        setErrorMsg("Secret Key ไม่สามารถออก Token ได้ กรุณาลองใหม่");
      }
    }
  };

  useEffect(() => {
    // ตั้งค่า mounted flag
    isMountedRef.current = true;

    const messageHandler = (event) => {
      try {
        const data = event?.data || {};
        if (
          data.type === "AUTH_TOKEN" &&
          data.headerKey === "Authorization" &&
          typeof data.headerValue === "string"
        ) {
          const tokenValue = String(data.headerValue);
          if (isMountedRef.current) {
            setInputKey(tokenValue);
            validateAndSet(tokenValue);
          }
        }
      } catch (error) {
        console.error("Error handling postMessage event:", error);
      }
    };

    window.addEventListener("message", messageHandler);

    const onKeyUpdate = () => {
      const current = getAccessToken();
      if (current && isMountedRef.current) {
        setAccessToken(current);
      }
    };

    window.addEventListener("accessToken:updated", onKeyUpdate);

    try {
      window.parent?.postMessage({ type: "REQUEST_API_KEY" }, "*");
    } catch (error) {
      console.error("Failed to postMessage to parent:", error);
    }

    const onTokenExpired = () => {
      if (isMountedRef.current) {
        clearAccessToken();
        setAccessToken("");
        setStatus("error");
        setErrorMsg("Token หมดอายุ กรุณาใส่ Secret Key ใหม่");
      }

      try {
        window.parent?.postMessage({ type: "REQUEST_VALID_TOKEN" }, "*");
      } catch (error) {
        console.error("Failed to postMessage to parent:", error);
      }
    };

    window.addEventListener("token:expired", onTokenExpired);

    const stored = getStoredAccessToken();
    if (stored) {
      validateAndSet(stored);
    }

    // FIX 3: Cleanup function ที่สมบูรณ์
    return () => {
      // ตั้งค่า mounted flag เป็น false
      isMountedRef.current = false;

      // ยกเลิก ongoing fetch requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // ลบ event listeners
      window.removeEventListener("message", messageHandler);
      window.removeEventListener("accessToken:updated", onKeyUpdate);
      window.removeEventListener("token:expired", onTokenExpired);
    };
  }, []);

  // Gate UI until a validated key is available
  if (status === "validating") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "70vh",
          fontFamily: "sans-serif",
          color: "#333",
        }}
        aria-busy="true"
        aria-live="polite"
      >
        <div style={{ textAlign: "center" }}>
          <div
            className="spin"
            style={{
              width: 48,
              height: 48,
              border: "4px solid #e5e7eb",
              borderTop: "4px solid #3b82f6",
              borderRadius: "50%",
              margin: "0 auto 12px",
            }}
          />
          <div style={{ fontSize: "0.95rem" }}>กำลังตรวจสอบ Key...</div>
          <style>
            {`
            @keyframes spin {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
            .spin { animation: spin 1s linear infinite; }
          `}
          </style>
        </div>
      </div>
    );
  }

  if (!accessToken) {
    return (
      <div
        style={{ maxWidth: 420, margin: "2rem auto", fontFamily: "sans-serif" }}
      >
        <h2>Enter Secret Key</h2>
        <input
          type="password"
          placeholder="SECRET-KEY"
          value={inputKey}
          onChange={(e) => setInputKey(e.target.value)}
          disabled={status === "validating"}
          style={{
            width: "100%",
            padding: "0.6rem",
            fontSize: "0.95rem",
            boxSizing: "border-box",
          }}
        />
        <button
          onClick={() => validateAndSet(inputKey, false)}
          disabled={status === "validating"}
          style={{
            width: "100%",
            padding: "0.6rem",
            marginTop: "0.6rem",
            fontSize: "0.95rem",
            cursor: status === "validating" ? "not-allowed" : "pointer",
          }}
        >
          {status === "validating" ? "กำลังตรวจสอบ Secret Key..." : "OK"}
        </button>

        {status === "error" && errorMsg && (
          <div
            style={{
              color: "#d32f2f",
              marginTop: "0.5rem",
              fontSize: "0.9rem",
            }}
          >
            {errorMsg}
          </div>
        )}
        <div
          style={{ marginTop: "0.5rem", fontSize: "0.85rem", color: "#666" }}
        >
          ใส่ Key ให้ถูกต้องเพื่อเข้าใช้งาน
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
